import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction, AuctionLot } from '../types/auction.types';
import { Bid } from '../types/bid.types';
import {
  formatTimeRemaining,
  getTimeRemaining,
  isInSoftCloseWindow,
} from '../utils/lotTiming';

/**
 * SaleConsole — Dan's live cockpit for the Shop Cleanout 050326 test.
 *
 * Single-screen view focused on what matters during a live HiBid-parallel sale:
 *   - Total lots / sold / unsold / extended
 *   - Top-line GMV (sum of current bids in dollars)
 *   - Live bid feed (most-recent first)
 *   - Lots closing in the next 5 minutes (sorted by remaining time)
 *   - Settlement-ready export button (CSV of lots with winning bidder)
 *
 * No mutation actions — this is the watch-floor view, not configuration.
 */

const fmt = (cents) => `$${((cents ?? 0) / 100).toFixed(2)}`;

const SaleConsole: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const [auction, setAuction] = useState<Auction | null>(null);
  const [bids, setBids] = useState<Bid[]>([]);
  const [tick, setTick] = useState(0);

  // Auction live listener.
  useEffect(() => {
    if (!auctionId) return;
    const unsub = onSnapshot(doc(db, 'auctions', auctionId), (snap) => {
      if (!snap.exists()) {
        setAuction(null);
        return;
      }
      const data = snap.data() as any;
      setAuction({
        id: snap.id,
        ...data,
        startDate: data.startDate?.toDate?.() ?? data.startDate,
        endDate: data.endDate?.toDate?.() ?? data.endDate,
        createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
        updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt,
        lots: (data.lots || []).map((l: any) => ({
          ...l,
          scheduledEndTime: l.scheduledEndTime?.toDate?.() ?? l.scheduledEndTime,
          actualEndTime: l.actualEndTime?.toDate?.() ?? l.actualEndTime,
          lastBidAt: l.lastBidAt?.toDate?.() ?? l.lastBidAt,
          lastExtensionAt: l.lastExtensionAt?.toDate?.() ?? l.lastExtensionAt,
        })),
      } as Auction);
    });
    return unsub;
  }, [auctionId]);

  // Recent-bids feed.
  useEffect(() => {
    if (!auctionId) return;
    const q = query(
      collection(db, 'bids'),
      where('auctionId', '==', auctionId),
      orderBy('placedAt', 'desc'),
      limit(50),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          ...data,
          placedAt: data.placedAt?.toDate?.() ?? data.placedAt,
        } as Bid;
      });
      setBids(list);
    });
    return unsub;
  }, [auctionId]);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const stats = useMemo(() => {
    if (!auction) return null;
    const lots = auction.lots || [];
    const totalLots = lots.length;
    const lotsWithBids = lots.filter((l) => (l.bidCount || 0) > 0).length;
    const lotsExtended = lots.filter((l) => (l.extensionCount || 0) > 0).length;
    const totalExtensions = lots.reduce((s, l) => s + (l.extensionCount || 0), 0);
    const grossCents = lots.reduce((s, l) => s + (l.currentBid || 0) * ((l.bidCount || 0) > 0 ? 1 : 0), 0);
    const closed = lots.filter((l) => {
      const e = l.actualEndTime || l.scheduledEndTime;
      return e ? new Date(e).getTime() <= Date.now() : false;
    }).length;
    return { totalLots, lotsWithBids, lotsExtended, totalExtensions, grossCents, closed };
  }, [auction, tick]);

  const closingSoon = useMemo<AuctionLot[]>(() => {
    if (!auction) return [];
    const lots = auction.lots || [];
    const FIVE_MIN = 5 * 60;
    return lots
      .map((l) => ({
        lot: l,
        remaining: l.actualEndTime || l.scheduledEndTime
          ? getTimeRemaining(new Date(l.actualEndTime || l.scheduledEndTime))
          : Number.POSITIVE_INFINITY,
      }))
      .filter((x) => x.remaining > 0 && x.remaining <= FIVE_MIN)
      .sort((a, b) => a.remaining - b.remaining)
      .slice(0, 20)
      .map((x) => x.lot);
  }, [auction, tick]);

  const exportSettlement = () => {
    if (!auction) return;
    const headers = [
      'lotNumber',
      'sku',
      'title',
      'startingBid',
      'currentBid',
      'bidCount',
      'extensionCount',
      'currentBidderId',
      'currentBidderNumber',
      'scheduledEndTime',
      'actualEndTime',
    ];
    const rows = (auction.lots || []).map((l) => [
      l.lotNumber,
      l.sku,
      JSON.stringify(l.title),
      l.startingBid != null ? (l.startingBid / 100).toFixed(2) : '',
      l.currentBid != null ? (l.currentBid / 100).toFixed(2) : '',
      l.bidCount ?? 0,
      l.extensionCount ?? 0,
      l.currentBidder ?? '',
      JSON.stringify(l.currentBidderNumber ?? ''),
      l.scheduledEndTime ? new Date(l.scheduledEndTime).toISOString() : '',
      l.actualEndTime ? new Date(l.actualEndTime).toISOString() : '',
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${auction.id}-settlement-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!auction) {
    return <div className="p-8">Loading sale console…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">{auction.title} — Live Console</h1>
          <p className="text-xs text-gray-500">
            Auction ID: {auction.id} · Closes {new Date(auction.endDate).toLocaleString()}
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/sale/${auction.id}`}
            target="_blank"
            rel="noreferrer"
            className="px-3 py-2 bg-gray-200 text-gray-800 text-sm rounded hover:bg-gray-300"
          >
            View public page →
          </Link>
          <button
            onClick={exportSettlement}
            className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            Export settlement CSV
          </button>
        </div>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <Stat label="Total lots" value={String(stats.totalLots)} />
          <Stat label="With bids" value={String(stats.lotsWithBids)} />
          <Stat label="Closed" value={String(stats.closed)} />
          <Stat label="Extended" value={String(stats.lotsExtended)} />
          <Stat label="Total extensions" value={String(stats.totalExtensions)} />
          <Stat label="Gross (current)" value={fmt(stats.grossCents)} highlight />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Closing soon */}
        <section className="bg-white border rounded p-4">
          <h2 className="font-semibold mb-2">Closing in next 5 minutes</h2>
          {closingSoon.length === 0 ? (
            <p className="text-sm text-gray-500">No lots closing soon.</p>
          ) : (
            <div className="space-y-1">
              {closingSoon.map((l) => {
                const end = l.actualEndTime || l.scheduledEndTime;
                const remaining = end ? getTimeRemaining(new Date(end)) : 0;
                const inSoft = end ? isInSoftCloseWindow(new Date(end), auction.softCloseWindow ?? 180) : false;
                const wasExt = (l.extensionCount || 0) > 0;
                return (
                  <div key={l.id} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                    <div className="truncate">
                      <span className="font-mono text-gray-500 mr-2">#{l.lotNumber}</span>
                      <span className="truncate">{l.title}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      {wasExt && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                          ×{l.extensionCount}
                        </span>
                      )}
                      <span className="text-xs text-gray-600">{fmt(l.currentBid ?? l.startingBid)}</span>
                      <span className={`font-mono text-xs ${inSoft ? 'text-red-600 font-bold' : 'text-gray-700'}`}>
                        {formatTimeRemaining(remaining)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Bid feed */}
        <section className="bg-white border rounded p-4">
          <h2 className="font-semibold mb-2">Live bid feed (last 50)</h2>
          {bids.length === 0 ? (
            <p className="text-sm text-gray-500">No bids yet.</p>
          ) : (
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {bids.map((b) => (
                <div key={b.id} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                  <div className="truncate">
                    <span className="font-mono text-gray-500 mr-2">#{b.lotNumber}</span>
                    <span className="truncate">{b.lotTitle}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="text-xs text-gray-600 truncate max-w-[140px]">{b.bidderName}</span>
                    <span className="font-bold">{fmt(b.amount)}</span>
                    <span className="text-xs text-gray-400">
                      {b.placedAt ? new Date(b.placedAt).toLocaleTimeString() : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`p-3 border rounded ${highlight ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="text-2xl font-bold">{value}</div>
  </div>
);

export default SaleConsole;
